<template>
  <widget-wrapper
    title="Sectores rentables"
    widget-id="widget-best-sectors"
    widget-name="Mejores sectores"
    :gd-url="gd_url + value_selected"
    :has-help="true"
    :help-url="url_iframe"
    :help-title="help_tittle"
    :has-alternate-action="true"
    alternate-action-icon="launch"
    alternate-action-url="/app/directionality"
    theme="dark"
    @close-widget="_closeWidget"
    @show-help="_sendDataToIframe"
  >
    <div v-if="loading" class="widget-loading">
      <div class="spinner"></div>
    </div>
    
    <div v-else class="best-sectors-content">
      <!-- Modern Time Period Selector -->
      <div class="time-period-tabs">
        <button 
          v-for="option in options" 
          :key="option.name"
          class="tab-button" 
          :class="{ active: value_selected === option.name }"
          @click="selectTimeframe(option.name)"
        >
          {{ option.value }}
        </button>
      </div>
      
      <!-- Modern Table -->
      <div class="modern-table-container">
        <table class="modern-table">
          <thead>
            <tr>
              <th>{{ table_header[0] }}</th>
              <th class="text-right">{{ table_header[1] }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in table_body" :key="index" class="sector-row">
              <td class="sector-name-cell" @click="redirectToSector(item.a)">
                <span class="sector-name-text">{{ item.a }}</span>
                <span class="material-icons sector-link-icon">open_in_new</span>
              </td>
              <td :class="getValueClass(item.b)">{{ formatProfitability(item.b) }}</td>
            </tr>
            <tr v-if="table_body.length === 0" class="empty-row">
              <td colspan="2">No hay sectores disponibles para este periodo</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </widget-wrapper>
</template>

<script>
import { mapGetters } from "vuex";
import APICaller from "@/components/codeComponents/APICaller.js";
import WidgetWrapper from "@/components/widgets/WidgetWrapper.vue";

export default {
  mixins: [APICaller],
  name: "BestSectors",
  components: {
    WidgetWrapper
  },
  data() {
    return {
      gd_url: "",
      options: [
        { name: "last_month", value: "Último mes" },
        { name: "last_three_months", value: "Últimos 3 meses" },
        { name: "last_six_months", value: "Últimos 6 meses" },
        { name: "last_year", value: "Último año" },
      ],
      value_selected: "last_month",
      table_header: ["Sector", "Rentabilidad"],
      table_body: [],
      widget_name: { id: "widget-best-sectors", name: "Mejores sectores" },
      loading: true,
      url_iframe: "https://player.vimeo.com/video/305716774?title=0&byline=0&portrait=0&autoplay=1",
      help_tittle: "Ayuda sobre mejores sectores",
      // Sector name translations (Spanish to English mapping)
      sectorTranslations: {
        // Main sectors
        "Servicios públicos": "Utilities",
        "Productos Básicos de Consumo": "Consumer Staples",
        "Finanzas": "Financials",
        "Materiales": "Materials",
        "Salud": "Health Care",
        "Industrial": "Industrials",
        "Bienes Raíces": "Real Estate",
        "Energía": "Energy",
        "Servicios de Comunicación": "Communication Services",
        "Consumo Discrecional": "Consumer Discretionary",
        "Tecnologías de la Información": "Information Technology",
        "Productos de Primera Necesidad": "Consumer Staples",
        
        // Sub-sectors
        "Distribución y Venta de Productos Básicos": "Consumer Staples Distribution & Retail",
        "Productos de Hogar y Personales": "Household & Personal Products",
        "Alimentos, Bebidas y Tabaco": "Food, Beverage & Tobacco",
        "Bancos": "Banks",
        "Finanzas Diversificadas": "Diversified Financials",
        "Seguros": "Insurance",
        "Servicios Financieros": "Financial Services",
        "Farmacéuticas, Biotecnología y Ciencias de la Vida": "Pharmaceuticals, Biotechnology & Life Sciences",
        "Equipos y Servicios de Salud": "Health Care Equipment & Services",
        "Servicios Comerciales y Profesionales": "Commercial & Professional Services",
        "Transporte": "Transportation",
        "Bienes de Capital": "Capital Goods",
        "Fideicomisos de Inversión en Bienes Raíces (REITs)": "Equity Real Estate Investment Trusts (REITs)",
        "Medios y Entretenimiento": "Media & Entertainment",
        "Servicios de Telecomunicaciones": "Telecommunication Services",
        "Bienes de Consumo Duraderos y Ropa": "Consumer Durables & Apparel",
        "Servicios al Consumidor": "Consumer Services",
        "Distribución y Venta de Consumo Discrecional": "Consumer Discretionary Distribution & Retail",
        "Semiconductores y Equipos Semiconductores": "Semiconductors & Semiconductor Equipment",
        "Hardware y Equipos Tecnológicos": "Technology Hardware & Equipment",
        "Software y Servicios": "Software & Services"
      }
    };
  },
  mounted() {
    this.gd_url = this.$detectEnvironment() + "/app/graficador-dinamico/best-sectors/";
    this._getBestSectors();
  },
  computed: {
    ...mapGetters(["is_mobile_device"]),
  },
  methods: {
    _sendDataToIframe: function() {
      this.$emit("url_iframe", this.url_iframe);
      this.$emit("help_tittle", this.help_tittle);
      this.$emit("load_iframe");
    },
    
    _getBestSectors() {
      this.loading = true;
      this.table_body = [];
      
      let success = (response) => {
        let bestSectors = response.data;
        Object.entries(bestSectors).forEach((e) => {
          var obj = {};
          obj["a"] = e[1].sector;
          obj["b"] = e[1].profitability;
          this.table_body.push(obj);
        });
        this.loading = false;
      };

      let failure = () => {
        this.loading = false;
      };

      let url = "/api/v1/widgets/get-best-sectors/" + this.value_selected;

      if (this.value_selected && this.value_selected != "") {
        const successHandler = new APICaller.SuccessHandler(success);
        const failureHandler = new APICaller.FailureHandler(failure, "139", "Error cargando mejores sectores.");
        const apiCallConfig = { loadingOverlay: false };

        this._getAPICall(url, successHandler, failureHandler, apiCallConfig);
      }
    },

    selectTimeframe(value) {
      this.value_selected = value;
      this._getBestSectors();
    },
    
    formatProfitability(value) {
      // Add a plus sign for positive values
      if (parseFloat(value) > 0) {
        return '+' + value + '%';
      }
      return value + '%';
    },
    
    getValueClass(value) {
      return {
        'positive': parseFloat(value) > 0,
        'negative': parseFloat(value) < 0,
        'neutral': parseFloat(value) === 0
      };
    },

    redirectToSector(sectorName) {
      // Get the English sector name for the URL
      const englishSectorName = this.getEnglishSectorName(sectorName);
      // Encode the sector name to handle special characters and spaces
      const encodedSectorName = encodeURIComponent(englishSectorName);
      // Open in new tab instead of redirecting current page
      window.open('/app/graficador-dinamico/sector/' + encodedSectorName, '_blank');
    },

    getEnglishSectorName(spanishName) {
      // First try to find a direct match in the translations
      if (this.sectorTranslations[spanishName]) {
        return this.sectorTranslations[spanishName];
      }
      
      // If no direct match, return the original name (fallback)
      return spanishName;
    },

    _closeWidget: function() {
      this.$emit("widget_name", this.widget_name);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/components/widgets/WidgetStyles.scss";

.widget-loading {
  @extend .widget-loading-base;
  background-color: var(--color-background, #0F0F1A);
}

.spinner {
  @extend .loading-spinner;
}

.best-sectors-content {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
}

.time-period-tabs {
  display: flex;
  gap: 8px;
  border-bottom: 1px solid var(--color-outline, rgba(255, 255, 255, 0.3));
  padding-bottom: 8px;
  
  .tab-button {
    background: none;
    border: none;
    color: var(--color-text-inactive, rgba(255, 255, 255, 0.6));
    font-size: 12px;
    padding: 6px 10px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      color: var(--color-text-primary, #FFFFFF);
      background-color: var(--color-active-surface, #193141);
    }
    
    &.active {
      color: var(--color-text-primary, #FFFFFF);
      background-color: var(--color-active-surface, #193141);
      font-weight: 500;
    }
  }
}

.modern-table-container {
  flex: 1;
  overflow-y: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  
  th {
    text-align: left;
    padding: 10px;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-secondary, #BFC2CD);
    border-bottom: 1px solid var(--color-outline, rgba(255, 255, 255, 0.3));
    position: sticky;
    top: 0;
    background-color: var(--color-surface, #20202B);
    
    &.text-right {
      text-align: right;
    }
  }
  
  td {
    padding: 10px;
    font-size: 13px;
    color: var(--color-text-primary, #FFFFFF);
  }
  
  tr {
    transition: background-color 0.15s ease;
    border-bottom: 1px solid var(--color-outline, rgba(255, 255, 255, 0.1));
    
    &:hover {
      background-color: var(--color-active-surface, #193141);
    }
    
    &.sector-row:hover {
      background-color: var(--color-active-surface, #193141);
    }
    
    &:last-child td {
      border-bottom: none;
    }
  }
  
  td:first-child {
    font-weight: 500;
  }
  
  .sector-name-cell {
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    
    &:hover {
      .sector-name-text {
        color: var(--color-highlight, #00aedd);
      }
      
      .sector-link-icon {
        opacity: 1;
      }
    }
  }
  
  .sector-name-text {
    flex: 1;
    transition: color 0.2s ease;
  }
  
  .sector-link-icon {
    font-size: 14px;
    color: var(--color-text-secondary, #BFC2CD);
    opacity: 0.6;
    transition: opacity 0.2s ease;
    margin-left: 8px;
  }
  
  td:last-child {
    text-align: right;
    font-weight: 600;
  }
  
  // Color styling for values
  td.positive {
    color: var(--primary, #00aedd);
  }
  
  td.negative {
    color: #ff5252;
  }
  
  td.neutral {
    color: var(--primary, #00aedd);
  }
  
  .empty-row td {
    text-align: center;
    color: var(--color-text-secondary, #BFC2CD);
    font-style: italic;
    padding: 20px 10px;
  }
}
</style>
